// frontend/src/hooks/useUnsavedChangesGuard.jsx
import { useEffect } from 'react';
import { useBlocker } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { useDirtyTabReporter } from '../tabs/useDirtyTabs';

/**
 * The browser-native "Leave site?" prompt on tab close / refresh while there
 * are unsaved changes. Any number of these can be live at once, so a dialog
 * with edits of its own takes this half alone.
 *
 * @param {boolean} isDirty - Whether there are unsaved changes
 */
export function useBeforeUnloadGuard(isDirty) {
  useEffect(() => {
    if (!isDirty) return;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);
}

/**
 * Guards a PAGE against accidental navigation when there are unsaved changes.
 *
 * Handles three ways of leaving:
 * 1. React Router navigation (sidebar, breadcrumbs, navigate()) — custom MUI dialog
 * 2. Browser tab close / refresh — `useBeforeUnloadGuard`
 * 3. In-app tab close and the new-version reload — the tab's unsaved-edits
 *    flag (tabs/useDirtyTabs)
 *
 * @param {boolean} isDirty - Whether the page has unsaved changes
 * @returns {JSX.Element|null} Dialog element to render in the component tree
 */
export function useUnsavedChangesGuard(isDirty) {
  const { t } = useTranslation();
  const blocker = useBlocker(isDirty);
  useBeforeUnloadGuard(isDirty);
  useDirtyTabReporter(isDirty);

  if (blocker.state !== 'blocked') return null;

  return (
    <ConfirmDialog
      open
      title={t('common.unsavedChangesTitle')}
      message={t('common.unsavedChangesNav')}
      confirmLabel={t('common.leavePage')}
      cancelLabel={t('common.stayOnPage')}
      variant="warning"
      onConfirm={() => blocker.proceed()}
      onCancel={() => blocker.reset()}
    />
  );
}
